<script>
import { ref } from 'vue'
import GraphCpuLoadSet from '@/components/graphs/Graph.Cpu.Load.Set.vue'
import GraphLicense from '@/components/graphs/Graph.License.vue'
import GraphDeviceRoleHistory from '@/components/graphs/Graph.Device.Role.History.vue'
import GraphCpuLoadLines from '@/components/graphs/Graph.Cpu.Load.Lines.vue'
import Uptime from '@/components/graphs/Uptime.vue'
import GraphDiskUsage from '@/components/graphs/Graph.Disk.Usage.vue'
import GraphNetworksBw from '@/components/graphs/Graph.Networks.Bw.vue'
import GraphNetworksPk from '@/components/graphs/Graph.Networks.Pk.vue'
import GraphNetworksPE from '@/components/graphs/Graph.Networks.PE.vue'
import MemoryUtilization from '@/components/graphs/Memory.Utilization.vue'
import CpuUtilization from '@/components/graphs/Graph.Cpu.Utilization.vue'
import LicenseSummaryTable from '@/components/LicenseSummaryTable.vue'
import api from '@/api'

export default {
  props: {
    deviceId: {
      type: Number,
      required: true,
    },
  },
  page: {
    title: 'Analytics Dashboard',
    meta: [
      {
        name: 'description',
      },
    ],
  },
  components: {
    GraphCpuLoadSet,
    GraphCpuLoadLines,
    Uptime,
    GraphDiskUsage,
    GraphNetworksBw,
    GraphNetworksPk,
    GraphNetworksPE,
    MemoryUtilization,
    GraphLicense,
    GraphDeviceRoleHistory,
    CpuUtilization,
    LicenseSummaryTable,
  },
  data() {
    return {
      pagetitle: 'Analytics Dashboard',
      device: null,
      deviceTypeTags: [],
      interfaces: [],
      visibleInterfaces: [],
      showHistory: true,
      pendingAlerts: 0,
    }
  },
  setup() {
    const refreshInterval = ref(60)
    return {
      refreshInterval,
    }
  },
  computed: {
    licenseInfo() {
      return this.device?.licenseInfo || null
    },
    showLicenseCard() {
      // Show for Ezemanage users even when license data is missing (table shows N/A)
      return this.authUserIsEzemanage && !!this.device
    },
    /** Device Role: Ezemanage users only, and device type must be tagged ezeha */
    showDeviceRole() {
      if (!this.authUserIsEzemanage) return false
      const tags = this.deviceTypeTags || []
      return tags.some((t) => String(t).toLowerCase() === 'ezeha')
    },
  },
  async mounted() {
    try {
      const deviceRes = await api.get(`/devices/${this.deviceId}`)
      this.device = deviceRes.data

      if (this.device?.host) {
        this.fetchPendingAlertCount(this.device.host)
      }

      const typeRes = await api.get(`/device-types/${this.device.deviceTypeId}`)
      const deviceType = typeRes.data.deviceType

      this.deviceTypeTags = deviceType?.tags?.length ? deviceType.tags : []

      // Always use the device type's CPU count for load graphs.
      if (deviceType?.cpuCount != null) {
        this.device = { ...this.device, cpuCount: deviceType.cpuCount }
      }

      if (deviceType?.details?.interfaces) {
        this.interfaces = deviceType.details.interfaces
        const stored = localStorage.getItem('visibleInterfaces') || ''
        this.visibleInterfaces = stored ? stored.split(',') : []
      }
    } catch (e) {
      console.error('Failed to load device data', e)
    }
  },
  methods: {
    toggleInterface(iface) {
      if (!this.visibleInterfaces.includes(iface)) {
        this.visibleInterfaces.push(iface)
        localStorage.setItem('visibleInterfaces', this.visibleInterfaces.join(','))
      } else {
        this.visibleInterfaces = this.visibleInterfaces.filter((i) => i !== iface)
        localStorage.setItem('visibleInterfaces', this.visibleInterfaces.join(','))
      }
    },
    showAlerts(agentHost) {
      this.$router.push({
        name: 'pending-alerts',
        query: { host: agentHost },
      })
    },
    async fetchPendingAlertCount(agentHost) {
      try {
        const res = await api.get('/alerts/pending', {
          params: { agentHost },
        })
        this.pendingAlerts = res.data.totalItems ?? 0
      } catch (err) {
        console.error('Failed to fetch pending alert count', err)
        this.pendingAlerts = 0
      }
    },
  },
}
</script>

<template>
  <Teleport to="#elePageTitle">
    {{ pagetitle }} -
    <span v-if="device" class="text-uppercase">{{ device.host }}</span>
  </Teleport>
  <Teleport to="#elePageAlert">
    <span
      class="alert-bell"
      :class="{ active: pendingAlerts > 0 }"
      title="Pending alerts"
      @click="showAlerts(device?.host)"
    >
      <i class="fa fa-bell"></i>
      <span class="alert-count">
        {{ pendingAlerts }}
      </span>
    </span>
  </Teleport>

  <div v-if="device" class="row align-items-stretch">
    <div class="col-12 col-md-2-5 d-flex">
      <Uptime :host="device.host" :refresh="refreshInterval" :type="device.protocolType" class="w-100" />
    </div>

    <div class="col-md-7 d-flex">
      <GraphCpuLoadSet
        :host="device.host"
        :refresh="refreshInterval"
        :cpuCount="device.cpuCount"
        :terms="['shortterm', 'midterm', 'longterm']"
        :type="device.protocolType"
        :dev-id="device.id"
        :history-link="showHistory"
        class="w-100"
      />
    </div>

    <div class="col-12 col-md-2-5 d-flex">
      <GraphLicense
        :host="device.host"
        :refresh="refreshInterval"
        :type="device.protocolType"
        :dev-id="device.id"
        :history-link="showHistory"
        class="w-100"
      />
    </div>
  </div>

  <div v-if="device" class="row">
    <div class="col-md-6 d-flex">
      <GraphDiskUsage
        :host="device.host"
        :refresh="refreshInterval"
        :type="device.protocolType"
        :dev-id="device.id"
        :history-link="showHistory"
      />
    </div>

    <div class="col-md-6 d-flex">
      <CpuUtilization
        :host="device.host"
        :refresh="refreshInterval"
        :type="device.protocolType"
        :dev-id="device.id"
        :history-link="showHistory"
      />
    </div>

    <div class="col-md-6">
      <GraphCpuLoadLines
        :host="device.host"
        :refresh="refreshInterval"
        :cpuCount="device.cpuCount"
        :type="device.protocolType"
        :dev-id="device.id"
        :history-link="showHistory"
      />
    </div>

    <div class="col-md-6">
      <MemoryUtilization
        :host="device.host"
        :refresh="refreshInterval"
        title="Memory Utilization"
        :type="device.protocolType"
        :dev-id="device.id"
        :cpuCount="device.cpuCount"
        :history-link="showHistory"
      />
    </div>
  </div>

  <!-- License + Device Role above Network Interfaces -->
  <div v-if="device && (showLicenseCard || showDeviceRole)" class="row mt-3 align-items-stretch">
    <div
      v-if="showLicenseCard"
      :class="showDeviceRole ? 'col-md-6 mb-3 mb-md-0' : 'col-md-12'"
    >
      <div class="card license-role-card">
        <div class="card-header">
          <h4 class="card-title mb-0">License Information</h4>
        </div>
        <div class="card-body license-scroll">
          <LicenseSummaryTable
            :license-info="licenseInfo"
            :device="device"
            :bordered="false"
            label-class="w-25"
            :include-device-type="true"
            :show-empty-as-na="true"
          />
        </div>
      </div>
    </div>

    <div
      v-if="showDeviceRole"
      :class="showLicenseCard ? 'col-md-6' : 'col-md-12'"
    >
      <div class="license-role-card device-role-wrap">
        <GraphDeviceRoleHistory
          title="Device Role (Today)"
          :host="device.host"
          :refresh="refreshInterval"
          :type="device.protocolType"
          :dev-id="device.id"
          :history-link="showHistory"
          duration-start="-1d"
          duration-end="0m"
          duration-interval="15m"
        />
      </div>
    </div>
  </div>

  <div v-if="device && interfaces.length" class="row mt-3">
    <div class="col-12 mb-2">
      <div class="section-divider">
        <h5 class="section-divider-title mb-0">Network Interfaces</h5>
        <p class="section-divider-hint text-muted mb-0">
          Interfaces configured on this device type
        </p>
      </div>
    </div>

    <div v-for="iface in interfaces" :key="iface.id" :ref="'iface_' + iface" class="col-md-6">
      <div v-if="iface !== 'lo'">
        <h5 class="cursor-pointer mb-3 d-flex justify-content-between align-center">
          <div @click="toggleInterface(iface)" class="cursor-pointer">
            <span v-if="visibleInterfaces.includes(iface)">-</span>
            <span v-else>+</span>
            <span class="ms-1">{{ iface }}</span>
          </div>
        </h5>
        <div v-if="visibleInterfaces.includes(iface)">
          <GraphNetworksBw
            :host="device.host"
            :refresh="refreshInterval"
            :title="`Network (Traffic)`"
            :iface="iface"
            :type="device.protocolType"
            :dev-id="device.id"
            :history-link="showHistory"
          />
          <GraphNetworksPk
            :host="device.host"
            :refresh="refreshInterval"
            :title="`Network (Packet) `"
            :iface="iface"
            :type="device.protocolType"
            :history-link="showHistory"
          />
          <GraphNetworksPE
            :host="device.host"
            :refresh="refreshInterval"
            :title="`Network (Packet - Error) `"
            :iface="iface"
            :type="device.protocolType"
            :history-link="showHistory"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media (min-width: 768px) {
  /* Split the five columns around CPU Loads equally without changing its col-md-7 width. */
  .col-md-2-5 {
    flex: 0 0 auto;
    width: 20.83333333%;
  }
}

.alert-bell {
  margin-left: 10px;
  cursor: pointer;
  position: relative;
}

.alert-bell i {
  font-size: 14px;
  color: #9aa0a6;
}

.alert-count {
  position: absolute;
  top: -4px;
  right: -5px;
  font-size: 9px;
  font-weight: 700;
  color: #9aa0a6;
}

.alert-bell.active .alert-count {
  font-size: 9px;
}

.alert-bell:hover i {
  color: #318cb6;
}

.alert-bell:hover .alert-count {
  color: #318cb6;
}

.section-divider {
  padding: 0.75rem 0;
  border-top: 1px solid rgba(126, 142, 177, 0.35);
  margin-top: 0.5rem;
}

.section-divider-title {
  font-size: 1rem;
  font-weight: 600;
}

.section-divider-hint {
  font-size: 0.8rem;
}

/* Match License and Device Role card heights */
.license-role-card {
  height: 380px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.license-role-card > .card-header {
  flex-shrink: 0;
}

.license-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.device-role-wrap :deep(.card) {
  height: 100%;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
}

.device-role-wrap :deep(.card-body) {
  flex: 1 1 auto;
  min-height: 0;
}

.device-role-wrap :deep(.graph),
.device-role-wrap :deep([ref='charRef']),
.device-role-wrap :deep(.card-body > .graph > div) {
  height: 100% !important;
  min-height: 0;
}
</style>
